/**
 * composerChipSync — a resolved handle in the DRAFT BOX becomes the person,
 * as you type (Miles, 2026-09-22).
 *
 * His ask, verbatim, twice: "when I go to mention someone in the text box
 * draft box like team, Malaika or Prest0n it doesn't turn into a special
 * element also like it's supposed to instantly to inform the user is putting
 * in the correct mention handle exactly and doesn't turn into an element if
 * the handle doesn't exist in the group thread / thread", and "a user
 * mentions someone it should be the same styles showing the full name".
 *
 * WHAT MAKES THIS SAFE TO DO AT ALL. The composer is an uncontrolled
 * contentEditable with a documented incident history, so three properties are
 * load-bearing and each is pinned:
 *
 *  1. THE SENT MESSAGE IS UNCHANGED. `htmlToPlainText` — the one door every
 *     reader of the draft passes through — emits `@handle` for a chip, and
 *     the chip carries that handle in its own text as well, so even a reader
 *     that knows nothing about chips still notifies the right person. The
 *     worst this feature can do to a message is nothing.
 *  2. EVERY EDIT GOES THROUGH `execCommand`, never innerHTML surgery. That
 *     keeps the browser's own undo stack (Ctrl+Z un-chips), fires the native
 *     input event the composer already listens to, and leaves the caret
 *     somewhere legal even if our own restore is wrong.
 *  3. THE CARET IS RESTORED IN ONE COORDINATE SYSTEM. A chip weighs exactly
 *     one character everywhere (CHIP_PLACEHOLDER), so "where the caret was"
 *     and "where the handles are" are measured against the same string.
 *
 * WHAT IT DELIBERATELY DOES NOT DO. It never touches the handle the caret is
 * inside — `chipCandidatesIn` skips it — because replacing text under a live
 * caret takes the caret with it: you would type "@mala", the chip would
 * swallow it, and "ika" would land after the chip. So a handle becomes a chip
 * the instant the caret LEAVES it (a space, a click, the next word), which is
 * instant by every measure a person can observe, and picking from the
 * dropdown is instant outright because the insert puts the caret past the
 * trailing space. The measured pill overlay stays for the handle still being
 * typed, so there is never a moment with no confirmation at all.
 */
import {
  chipCandidatesIn,
  composerChipHtml,
  handleOfChipClass,
  isComposerChip,
} from './composerChip';
import { flattenComposerText, pointAt } from './composerMentionPills';
import type { FlatComposerText } from './composerMentionPills';
import type { MentionChipPerson } from './mentionChip';

/** The caret as an offset into the flattened draft, chips counting as one. */
function readFlatCaret(root: Node, flat: FlatComposerText): number | null {
  const sel = typeof window !== 'undefined' ? window.getSelection() : null;
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.startContainer)) return null;

  // A caret in a TEXT node: find the segment it belongs to.
  if (range.startContainer.nodeType === 3) {
    for (const seg of flat.segments) {
      if (seg.node === range.startContainer) return seg.start + range.startOffset;
    }
    return null;
  }
  // A caret between elements: the child to its left decides.
  const container = range.startContainer;
  const before = container.childNodes[range.startOffset - 1] ?? null;
  // Nothing to its left. That is flat offset 0 only at the TOP of the
  // composer; inside a nested block it means "the start of this block", which
  // is some other offset entirely — and writing 0 there would teleport the
  // caret to the top of the draft. Unknown is the honest answer, and it
  // leaves the browser's own post-edit caret standing.
  if (!before) return container === root ? 0 : null;
  for (const seg of flat.segments) {
    if (seg.node === before) {
      return seg.start + (seg.chipLength ?? (seg.node.textContent ?? '').length);
    }
  }
  return null;
}

/** Put the caret at a flattened offset. Best-effort: a miss leaves it alone. */
function writeFlatCaret(root: Node, offset: number): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const flat = flattenComposerText(root);
  const clamped = Math.max(0, Math.min(offset, flat.text.length));
  const point = pointAt(flat.segments, clamped);
  if (!point) return;
  try {
    const range = document.createRange();
    range.setStart(point.node, point.offset);
    range.collapse(true);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  } catch {
    /* best-effort — the browser's own post-execCommand caret stands */
  }
}

/**
 * Replace a range of the flattened draft, natively.
 *
 * `insertHTML` for a chip and `insertText` for the melt back to a handle —
 * never insertHTML for both. A melted handle is read out of a class
 * attribute, and the one thing a class attribute must never be is a path
 * into an html sink: `insertText` has no markup to interpret, so the
 * question does not arise however that attribute got there.
 */
function replaceRange(
  flat: FlatComposerText,
  start: number,
  end: number,
  value: string,
  as: 'html' | 'text',
): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  const from = pointAt(flat.segments, start);
  const to = pointAt(flat.segments, end);
  if (!from || !to) return false;
  try {
    const range = document.createRange();
    range.setStart(from.node, from.offset);
    range.setEnd(to.node, to.offset);
    const sel = window.getSelection();
    if (!sel) return false;
    sel.removeAllRanges();
    sel.addRange(range);
    return document.execCommand(as === 'html' ? 'insertHTML' : 'insertText', false, value);
  } catch {
    return false;
  }
}

/**
 * A chip whose handle no longer resolves is no longer a confirmation.
 *
 * It happens for real: a permitted teammate can take someone out of the
 * thread (T-G1) while a draft naming them sits in the box. Leaving the chip
 * would promise a delivery that will not happen, so it melts back to the
 * plain `@handle` the person typed — which is exactly what an unresolved
 * handle looks like, and still what gets sent.
 */
function firstStaleChip(
  flat: FlatComposerText,
  resolve: (handle: string) => MentionChipPerson | null,
): { start: number; handle: string } | null {
  for (const seg of flat.segments) {
    if (!seg.chipLength) continue;
    const el = seg.node as Element;
    if (!isComposerChip(el)) continue;
    const handle = handleOfChipClass(el.className);
    if (!handle) continue;
    const person = resolve(handle);
    if (!person || !String(person.name ?? '').trim()) return { start: seg.start, handle };
  }
  return null;
}

/**
 * How many edits one pass may make.
 *
 * Every iteration re-reads the live DOM, so this is a stop, not a budget: a
 * keystroke produces at most one new chip and a paste a handful. It exists so
 * that a resolver which somehow disagrees with itself between two calls
 * cannot spin the composer.
 */
const MAX_EDITS_PER_SYNC = 24;

/**
 * Bring the draft box's chips into line with what resolves, once.
 *
 * Returns the number of edits it made, which is what a test asserts on and
 * what a caller can log. Given a composer the person is not currently in, the
 * CALLER is responsible for not calling this at all — see the hook.
 */
export function syncComposerChips(
  el: HTMLElement,
  resolve: (handle: string) => MentionChipPerson | null,
): number {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
  let edits = 0;
  // RE-READ THE DOM EVERY ITERATION, and that is not caution — it is
  // required. `execCommand` fires a native input event synchronously, the
  // composer's own `handleInput` sanitizes on it, and the sanitizer stamps
  // `contenteditable` onto the chip we just wrote. That counts as a
  // change, so it re-seats `innerHTML` wholesale and every node in a map
  // taken before the edit is now detached. A loop that planned all its
  // edits up front would be writing into a document that no longer
  // exists; this one only ever holds a map for the single edit it is
  // about to make.
  for (let pass = 0; pass < MAX_EDITS_PER_SYNC; pass += 1) {
    const flat = flattenComposerText(el);
    const caret = readFlatCaret(el, flat);

    // A chip that stopped resolving goes back to being a handle, first —
    // so the same pass can then judge that handle on its own merits.
    const stale = firstStaleChip(flat, resolve);
    if (stale) {
      // PAD ONLY WHERE THE TEXT DOES NOT ALREADY. The chip occupied one
      // character between whatever sat either side of it; a handle needs
      // whitespace before it to open the grammar at all, and whitespace after
      // it so the following word does not run on. Adding both unconditionally
      // is how "Per <chip> today" melts to "Per @justin  today" — a double
      // space the person never typed, in their own draft.
      const before = stale.start > 0 ? flat.text[stale.start - 1] : ' ';
      const after = stale.start + 1 < flat.text.length ? flat.text[stale.start + 1] : ' ';
      const text = `${/\s/.test(before) ? '' : ' '}@${stale.handle}${/\s/.test(after) ? '' : ' '}`;
      if (!replaceRange(flat, stale.start, stale.start + 1, text, 'text')) break;
      if (caret !== null) {
        writeFlatCaret(el, stale.start < caret ? caret + text.length - 1 : caret);
      }
      edits += 1;
      continue;
    }

    if (flat.text.indexOf('@') === -1) break;
    const candidates = chipCandidatesIn(flat.text, caret, resolve);
    if (candidates.length === 0) break;

    // The LAST one, so the offsets of the others are still describing the
    // text they were read from when the next iteration re-reads it.
    const c = candidates[candidates.length - 1];
    const html = composerChipHtml(c.handle, c.person);
    if (!html || !replaceRange(flat, c.start, c.end, html, 'html')) break;

    // The chip weighs one character where the handle weighed its length.
    // execCommand has parked the caret after the chip it just wrote,
    // which is only where the person was if they were typing right there.
    if (caret !== null) {
      writeFlatCaret(el, c.end <= caret ? caret - (c.end - c.start) + 1 : caret);
    }
    edits += 1;
  }
  return edits;
}
